import {
  BooleanInput,
  FormDataConsumer,
  FormTab,
  Record,
  required,
  TabbedForm,
  TextInput,
  useTranslate,
} from "react-admin";
import { useForm } from "react-final-form";
import {
  CreateVirtualTenantDto,
  TenantOutputDto,
  VirtualTenantDto,
} from "../../../common/data/service-proxies";
import { RaTenantPicker } from "../../common/form/RaTenantPicker";
import { VehicleWithPeriodSelectionGrid } from "./VehicleWithPeriodSelectionGrid";
import { FormToolbar } from "../../common/form/FormToolbar";
import { UsersSelectionForm } from "./UsersSelectionForm";
import { DriverSelectionGrid } from "./DriverSelectionGrid";

interface Props {
  record?: VirtualTenantDto & Record;
  isEdit?: boolean;
}

interface VirtualTenantPickerInputProps {
  isEdit?: boolean;
}

export const normalizeVirtualTenantRecord = (
  record?: VirtualTenantDto & Record
) => {
  if (!record) {
    return record;
  }

  const { drivers: _drivers, ...data } = record;
  return {
    ...data,
    driverIds: (record.drivers ?? [])
      .map((driver) => driver.id)
      .filter((id): id is number => typeof id === "number"),
    vehicles: (record.vehicles ?? [])
      .filter((vehicle) => typeof vehicle.vehicleId === "number")
      .map((vehicle) => ({
        vehicleId: vehicle.vehicleId,
        startTime: vehicle.startTime ?? null,
        endTime: vehicle.endTime ?? null,
      })),
  };
};

export const transformVirtualTenantRecord = (
  record: Record
): CreateVirtualTenantDto => {
  const isOverridden = record.isOverridden === true;

  return {
    tenantId: record.tenantId,
    name: record.name,
    isOverridden,
    overriddenCompany: isOverridden ? record.overriddenCompany?.trim() : null,
    overriddenDot: isOverridden ? record.overriddenDot?.trim() : null,
    users: record.users ?? [],
    vehicles: (record.vehicles ?? []).map((vehicle: any) => ({
      vehicleId: Number(vehicle.vehicleId),
      startTime: vehicle.startTime ?? null,
      endTime: vehicle.endTime ?? null,
    })),
    driverIds: (record.driverIds ?? []).map(Number),
  };
};

const VirtualTenantPickerInput = ({
  isEdit,
}: VirtualTenantPickerInputProps) => {
  const form = useForm();

  const handleTenantSelected = (tenant: TenantOutputDto | null) => {
    const values = form.getState().values;

    if (values.isOverridden === true) {
      if (!values.overriddenCompany) {
        form.change("overriddenCompany", tenant?.name ?? null);
      }

      if (!values.overriddenDot) {
        form.change("overriddenDot", tenant?.dot ?? null);
      }

      return;
    }

    form.change("overriddenCompany", tenant?.name ?? null);
    form.change("overriddenDot", tenant?.dot ?? null);
  };

  return (
    <RaTenantPicker
      disabled={isEdit}
      label="Customer to use"
      source="tenantId"
      validate={[required()]}
      onSelectedRecord={handleTenantSelected}
    />
  );
};

export const VirtualTenantForm = ({ isEdit, record, ...props }: Props) => {
  const translate = useTranslate();
  const normalizedRecord = normalizeVirtualTenantRecord(record);

  const validateVirtualTenantForm = (values: Record) => {
    const errors: any = {};

    if (values.isOverridden === true) {
      const overriddenCompany = values.overriddenCompany?.trim();
      const overriddenDot = values.overriddenDot?.trim();

      if (!overriddenCompany) {
        errors.overriddenCompany = translate("ra.validation.required");
      }

      if (!overriddenDot) {
        errors.overriddenDot = translate("ra.validation.required");
      } else if (!/^\d+$/.test(overriddenDot)) {
        errors.overriddenDot = translate(
          "resources.common.labels.mustBeNumericOnly"
        );
      }
    }

    return errors;
  };

  return (
    <TabbedForm
      variant="outlined"
      className="form-create-edit"
      {...props}
      record={normalizedRecord}
      validate={validateVirtualTenantForm}
      toolbar={<FormToolbar alwaysEnableSaveButton />}
    >
      <FormTab label="Customer">
        <VirtualTenantPickerInput isEdit={isEdit} />
        <BooleanInput
          source="isOverridden"
          label="resources.admin-virtual-tenants.labels.useDefaultValues"
          defaultValue={false}
          format={(value) => value !== true}
          parse={(value) => !value}
        />
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData.isOverridden !== true ? null : (
              <>
                <TextInput
                  {...rest}
                  source="overriddenCompany"
                  label="resources.admin-virtual-tenants.labels.customerName"
                />
                <TextInput
                  {...rest}
                  source="overriddenDot"
                  label="resources.admin-virtual-tenants.labels.dot"
                />
              </>
            )
          }
        </FormDataConsumer>
      </FormTab>
      <FormTab label="Users">
        <UsersSelectionForm />
      </FormTab>
      <FormTab label="Vehicles">
        <VehicleWithPeriodSelectionGrid />
      </FormTab>
      <FormTab label="Drivers">
        <DriverSelectionGrid isPeriodEnabled />
      </FormTab>
    </TabbedForm>
  );
};
